import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ACCEPT_CV, type CvDejaConnu } from '@rh/shared';
import { controleLocalCv, envoyerCv, messageEnvoiCv, messageResultatDepot } from '../envoi-cv';
import { nomComplet } from '../format';

/** Relecture de la fiche après un dépôt : texte extrait, adéquation et coordonnées lues arrivent du worker. */
export const DELAI_RELECTURE_APRES_DEPOT_MS = 20_000;

interface Props {
  candidatureId: string;
  aUnCv: boolean;
  /** `documentId` du CV à ouvrir aussitôt ; null pour la relecture différée. */
  onDepose: (documentId: string | null) => void;
}

/**
 * « Déposer le CV » sur la fiche : le fichier est rattaché à cette candidature
 * (docs/API.md § CV à rattacher). Un seul fichier ; ni `multiple` ni `capture`,
 * qui fermerait l'accès aux Téléchargements du téléphone.
 */
export function DeposerCv({ candidatureId, aUnCv, onDepose }: Props) {
  const [envoi, setEnvoi] = useState(false);
  const [resultat, setResultat] = useState<{ texte: string; alerte: boolean } | null>(null);
  const [aussiSur, setAussiSur] = useState<CvDejaConnu[]>([]);
  const [relecture, setRelecture] = useState(0);
  const controleur = useRef<AbortController | null>(null);
  const rappel = useRef(onDepose);

  useEffect(() => {
    rappel.current = onDepose;
  }, [onDepose]);

  // Envoi abandonné si la fiche se ferme.
  useEffect(() => {
    const envois = controleur;
    return () => envois.current?.abort();
  }, []);

  // Seconde lecture de la fiche, annulée si elle se ferme avant.
  useEffect(() => {
    if (relecture === 0) return;
    const minuteur = setTimeout(() => rappel.current(null), DELAI_RELECTURE_APRES_DEPOT_MS);
    return () => clearTimeout(minuteur);
  }, [relecture]);

  async function deposer(fichier: File | undefined): Promise<void> {
    if (!fichier || envoi) return;
    setResultat(null);
    setAussiSur([]);
    const refus = controleLocalCv(fichier);
    if (refus) {
      setResultat({ texte: refus, alerte: true });
      return;
    }
    const c = new AbortController();
    controleur.current = c;
    setEnvoi(true);
    try {
      const reponse = await envoyerCv(`/candidatures/${candidatureId}/documents`, fichier, c.signal);
      setResultat({ texte: messageResultatDepot(reponse), alerte: reponse.resultat === 'deja_present' });
      setAussiSur(reponse.aussiSur);
      onDepose(reponse.documentId);
      if (reponse.resultat !== 'deja_present') setRelecture((n) => n + 1);
    } catch (erreur) {
      const classe = messageEnvoiCv(erreur);
      if (classe) setResultat({ texte: classe.message, alerte: true });
    } finally {
      if (!c.signal.aborted) setEnvoi(false);
    }
  }

  return (
    <>
      <label className={`bouton-second lien-bouton bouton-fichier${envoi ? ' inactif' : ''}`}>
        {envoi ? 'Envoi…' : aUnCv ? 'Déposer un autre CV' : 'Déposer le CV'}
        <input
          type="file"
          accept={ACCEPT_CV}
          disabled={envoi}
          onChange={(e) => {
            void deposer(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </label>
      {resultat && (
        <p className={resultat.alerte ? 'resultat-depot alerte' : 'resultat-depot'} role="status">
          {resultat.texte}
        </p>
      )}
      {aussiSur.length > 0 && (
        <p className="resultat-depot mention">
          Le même fichier est aussi sur :{' '}
          {aussiSur.map((fiche, index) => (
            <span key={fiche.candidatureId}>
              {index > 0 && ', '}
              <Link to={`/candidatures/${fiche.candidatureId}`}>
                {nomComplet(fiche)} — {fiche.annonceIntitule}
              </Link>
            </span>
          ))}
        </p>
      )}
    </>
  );
}
